import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, MessageCircle, Gift, Check } from "lucide-react";
import { PageShell } from "../components/PageShell";
import { Reveal, MaskLines } from "../components/Reveal";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { useLang, WHATSAPP } from "../lib/i18n";
import { TREATMENTS } from "../lib/treatments";
import { useSeo } from "../lib/seo";

const ease = [0.22, 1, 0.36, 1];
const UI = {
  it: { back: "Tutti i trattamenti", book: "Prenota questo trattamento", wa: "Chiedi a Gioia su WhatsApp", forWho: "È per te se cerchi", how: "Come si svolge", faq: "Domande frequenti", gift: "Regalalo con un buono", ctaT: "Il primo passo è una consulenza.", ctaS: "Ti ascoltiamo, valutiamo insieme e costruiamo il percorso giusto per te.", note: "I risultati variano da persona a persona. Ogni percorso viene definito dopo una consulenza personalizzata.", missing: "Trattamento non trovato.", in: "a Catanzaro", localT: (x) => `${x} a Catanzaro, da Gioia`, local: (x) => `Cerchi ${x.toLowerCase()} a Catanzaro? Gioia Centro Estetico ti accoglie in Via Tre Fontane 37, con un percorso costruito su di te dopo una consulenza personalizzata. Valutata 4,8 su Google dalle sue clienti, Gioia unisce metodo, ascolto e prodotti professionali. Puoi prenotare online o scriverci su WhatsApp.`, others: "Altri trattamenti a Catanzaro", all: "Il centro estetico" },
  en: { back: "All treatments", book: "Book this treatment", wa: "Ask Gioia on WhatsApp", forWho: "It's for you if you want", how: "How it works", faq: "Frequently asked questions", gift: "Give it as a gift card", ctaT: "The first step is a consultation.", ctaS: "We listen, assess together and build the right programme for you.", note: "Results vary from person to person. Every programme is defined after a personal consultation.", missing: "Treatment not found.", in: "in Catanzaro", localT: (x) => `${x} in Catanzaro, at Gioia`, local: (x) => `Looking for ${x.toLowerCase()} in Catanzaro? Gioia Centro Estetico welcomes you at Via Tre Fontane 37, with a programme built around you after a personal consultation. Rated 4.8 on Google by her clients, Gioia combines method, care and professional products. Book online or message us on WhatsApp.`, others: "More treatments in Catanzaro", all: "The beauty centre" },
};

const Hero = ({ c, tr, u, onBook }) => (
  <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
    <div className="lg:col-span-7">
      <Link to="/#services" data-testid="treatment-back-link" className="inline-flex items-center gap-2 text-[0.72rem] tracking-[0.2em] uppercase text-[#6b625a] hover:text-[#8A6A3A] transition-colors duration-300">
        <ArrowLeft className="w-4 h-4" /> {u.back}
      </Link>
      <p className="overline text-[#8A6A3A] mt-10">{c.kicker} · Catanzaro</p>
      <h1 className="mt-5 font-serif-d font-light text-[3.2rem] sm:text-7xl lg:text-8xl leading-[0.92] text-[#14110F]" data-testid="treatment-title">
        <MaskLines lines={[c.title]} delay={0.1} />
      </h1>
      <p className="mt-8 text-[#4f4841] font-light leading-relaxed max-w-xl text-base sm:text-lg" data-testid="treatment-lead">{c.lead}</p>
      <div className="mt-10 flex flex-col sm:flex-row gap-4">
        <button type="button" onClick={onBook} data-testid="treatment-book-btn" className="group inline-flex justify-center items-center gap-3 rounded-full bg-[#14110F] text-[#F6F2EC] px-8 py-4 text-[0.75rem] tracking-[0.2em] uppercase transition-colors duration-500 hover:bg-[#8A6A3A]">
          {u.book} <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" />
        </button>
        <a href={`${WHATSAPP}?text=${encodeURIComponent(`Ciao Gioia, vorrei informazioni su: ${c.title}`)}`} target="_blank" rel="noreferrer" data-testid="treatment-whatsapp-btn" className="inline-flex justify-center items-center gap-2 rounded-full border border-[#14110F]/25 text-[#14110F] px-7 py-4 text-[0.72rem] tracking-[0.2em] uppercase transition-colors duration-500 hover:border-[#8A6A3A] hover:text-[#8A6A3A]">
          <MessageCircle className="w-4 h-4" /> {u.wa}
        </a>
      </div>
    </div>
    <div className="lg:col-span-5">
      <motion.div initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, ease }} className="relative overflow-hidden aspect-[4/5] rounded-t-[999px] bg-[#14110F]">
        <img src={tr.img} alt={c.title} style={{ objectPosition: tr.pos }} className="absolute inset-0 w-full h-full object-cover" data-testid="treatment-image" />
      </motion.div>
    </div>
  </div>
);

const ForWho = ({ c, u }) => (
  <div className="grid lg:grid-cols-12 gap-8">
    <Reveal className="lg:col-span-4"><h2 className="font-serif-d font-light text-4xl sm:text-5xl text-[#14110F] leading-[1]">{u.forWho}</h2></Reveal>
    <ul className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-[#ddd4c7] border border-[#ddd4c7]" data-testid="treatment-for-who">
      {c.forWho.map((x, i) => (
        <Reveal as="li" key={x} delay={i * 0.06} className="bg-[#F6F2EC] p-6 flex gap-4 items-start">
          <Check className="w-5 h-5 mt-0.5 text-[#8A6A3A] shrink-0" strokeWidth={1.5} />
          <span className="text-[#14110F] font-light">{x}</span>
        </Reveal>
      ))}
    </ul>
  </div>
);

const Steps = ({ c, u }) => (
  <div className="bg-[#14110F] grain text-[#F6F2EC] -mx-5 sm:mx-0 px-6 sm:px-12 lg:px-16 py-16 sm:py-20">
    <Reveal><h2 className="font-serif-d font-light text-4xl sm:text-5xl leading-[1]">{u.how}</h2></Reveal>
    <ol className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-10" data-testid="treatment-steps">
      {c.steps.map(([t, d], i) => (
        <Reveal as="li" key={t} delay={i * 0.08} className="border-t border-[#F6F2EC]/20 pt-6">
          <span className="font-stencil tracking-[0.25em] text-[#C9A66B]">0{i + 1}</span>
          <h3 className="mt-4 font-serif-d text-2xl sm:text-3xl font-light">{t}</h3>
          <p className="mt-3 text-sm text-[#d9d0c4] font-light leading-relaxed">{d}</p>
        </Reveal>
      ))}
    </ol>
  </div>
);

const Faq = ({ c, u }) => (
  <div className="grid lg:grid-cols-12 gap-8">
    <Reveal className="lg:col-span-4"><h2 className="font-serif-d font-light text-4xl sm:text-5xl text-[#14110F] leading-[1]">{u.faq}</h2></Reveal>
    <Accordion type="single" collapsible className="lg:col-span-8 border-t border-[#ddd4c7]" data-testid="treatment-faq">
      {c.faq.map(([q, a], i) => (
        <AccordionItem key={q} value={`q${i}`} className="border-[#ddd4c7]">
          <AccordionTrigger data-testid={`treatment-faq-trigger-${i}`} className="py-6 font-serif-d font-light text-xl sm:text-2xl text-[#14110F] hover:no-underline hover:text-[#8A6A3A] transition-colors duration-300">{q}</AccordionTrigger>
          <AccordionContent data-testid={`treatment-faq-answer-${i}`} className="text-base text-[#4f4841] font-light leading-relaxed pr-8">{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </div>
);

const Local = ({ c, u, slug, lang }) => (
  <div className="grid lg:grid-cols-12 gap-8" data-testid="treatment-local">
    <Reveal className="lg:col-span-5"><h2 className="font-serif-d font-light text-4xl sm:text-5xl text-[#14110F] leading-[1]">{u.localT(c.title)}</h2></Reveal>
    <Reveal delay={0.08} className="lg:col-span-7">
      <p className="text-[#4f4841] font-light leading-relaxed">{u.local(c.title)}</p>
      <p className="overline text-[0.62rem] text-[#6b625a] mt-10 mb-4">{u.others}</p>
      <ul className="flex flex-wrap gap-2">
        {Object.keys(TREATMENTS).filter((k) => k !== slug).map((k) => (
          <li key={k}>
            <Link to={`/trattamenti/${k}`} data-testid={`treatment-related-${k}`} className="inline-flex text-xs px-3 py-1.5 rounded-full border border-[#ddd4c7] text-[#4f4841] hover:border-[#8A6A3A] hover:text-[#8A6A3A] transition-colors duration-300">
              {TREATMENTS[k][lang].title} {u.in}
            </Link>
          </li>
        ))}
        <li><Link to="/centro-estetico-catanzaro" data-testid="treatment-related-hub" className="inline-flex text-xs px-3 py-1.5 rounded-full bg-[#14110F] text-[#F6F2EC] hover:bg-[#8A6A3A] transition-colors duration-300">{u.all} {u.in}</Link></li>
      </ul>
    </Reveal>
  </div>
);

const Cta = ({ u, onBook }) => (
  <div className="border border-[#ddd4c7] p-8 sm:p-14 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
    <div className="max-w-xl">
      <h2 className="font-serif-d font-light text-4xl sm:text-5xl text-[#14110F] leading-[1]">{u.ctaT}</h2>
      <p className="mt-4 text-[#4f4841] font-light">{u.ctaS}</p>
      <p className="mt-6 text-xs text-[#6b625a]">{u.note}</p>
    </div>
    <div className="flex flex-col sm:flex-row gap-4 shrink-0">
      <button type="button" onClick={onBook} data-testid="treatment-cta-book-btn" className="group inline-flex justify-center items-center gap-3 rounded-full bg-[#C9A66B] text-[#14110F] px-8 py-4 text-[0.75rem] tracking-[0.2em] uppercase font-medium transition-colors duration-500 hover:bg-[#14110F] hover:text-[#F6F2EC]">
        {u.book} <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" />
      </button>
      <Link to="/#gift" data-testid="treatment-gift-link" className="inline-flex justify-center items-center gap-2 rounded-full border border-[#14110F]/25 text-[#14110F] px-7 py-4 text-[0.72rem] tracking-[0.2em] uppercase transition-colors duration-500 hover:border-[#8A6A3A] hover:text-[#8A6A3A]">
        <Gift className="w-4 h-4" /> {u.gift}
      </Link>
    </div>
  </div>
);

export default function Treatment() {
  const { slug } = useParams();
  const { lang } = useLang();
  const navigate = useNavigate();
  const tr = TREATMENTS[slug];
  const c = tr?.[lang];
  const u = UI[lang] ?? UI.it;
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  useSeo(
    c ? `${c.title} ${u.in} · Gioia Centro Estetico` : "Gioia Centro Estetico",
    c ? `${c.title} ${u.in}: ${c.lead}`.slice(0, 157).replace(/\s\S*$/, "") + "…" : undefined,
  );
  const onBook = () => navigate("/#booking", { state: { service: tr.service[lang] } });

  return (
    <PageShell>
      <div className="max-w-[1280px] mx-auto space-y-24 sm:space-y-32" data-testid="treatment-page">
        {c ? (
          <>
            <Hero c={c} tr={tr} u={u} onBook={onBook} />
            <ForWho c={c} u={u} />
            <Steps c={c} u={u} />
            <Faq c={c} u={u} />
            <Local c={c} u={u} slug={slug} lang={lang} />
            <Cta u={u} onBook={onBook} />
          </>
        ) : (
          <p data-testid="treatment-not-found" className="font-serif-d text-3xl text-[#14110F]">{u.missing}</p>
        )}
      </div>
    </PageShell>
  );
}
